import React, { useCallback } from 'react';
import { identity, head } from 'ramda';

import {
  ADDRESS,
  CAD,
  CHUTE,
  TYPE,
  WINDOW,
  PRIO,
  PARCEL_NUMBER,
  KING,
  LOAD,
  KG,
  REQ,
  MANIFEST_LOCATION,
} from '../../../constants/strings';
import { COLUMN_ACCESSORS as CA } from '../../../constants/table';
import BodyCell from '../../common/Table/BodyCell';
import HeaderCell from '../../common/Table/HeaderCell';
import { Add, RadioIcon, Remove, SpotIcon, Defer } from '../../icons';
import palette from '../../styles/palette';
import {
  HighValueItem,
  ManifestLocationItem,
  ServiceItem,
} from '../../common/Table/Items';
import {
  gramsToKg,
  isRemoveRequest,
  isDeferredRequest,
  isRequestHasResponse,
  getCadScannedStatusColor,
  getCadStatusTextColor,
} from '../../../features/routeDetail/model';
import { UNIVERSE_TRACKING_LINK_BASE } from '../../../constants/app';
import { getParcelRequestString } from '../../../models/routeDetail';
import WarningWithTooltip from '../../WarningWithTooltip';
import StopInfoItem from '../../common/Table/StopInfoItem';

const parcelDetailsColumns = ({
  columnsConfigs,
  headerCellClasses,
  bodyCellClasses,
  modifiers: {
    onUpdateKingParcel = identity,
    onParcelRequestClick = identity,
    onParcelNumberClick = identity,
  } = {},
  isKingParcelColumnDisabled,
}) => [
  {
    Cell: ({ rowData }) => (
      <HighValueItem rowData={rowData} classes={bodyCellClasses} />
    ),
    columnConfig: columnsConfigs[CA.HIGH_VALUE],
    accessor: CA.HIGH_VALUE,
  },
  {
    Cell: ({ value }) => (
      <BodyCell
        value={<ServiceItem value={value} />}
        classes={bodyCellClasses}
        extraContainerClasses={[bodyCellClasses.smallPadding]}
      />
    ),
    columnConfig: columnsConfigs[CA.SERVICE],
    accessor: CA.SERVICE,
  },
  {
    Cell: ({ value }) => (
      <BodyCell
        value={<StopInfoItem value={value} />}
        classes={bodyCellClasses}
        extraContainerClasses={[bodyCellClasses.cellIcon]}
        extraCellClasses={[bodyCellClasses.cellCenter]}
      />
    ),
    columnConfig: columnsConfigs[CA.PARCELS_INFO],
    accessor: CA.PARCELS_INFO,
  },
  {
    Header: () => (
      <HeaderCell
        header={PRIO}
        classes={headerCellClasses}
        extraContainerClasses={[headerCellClasses.smallPadding]}
      />
    ),
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraContainerClasses={[bodyCellClasses.smallPadding]}
      />
    ),
    columnConfig: columnsConfigs[CA.PRIORITY],
    accessor: CA.PRIORITY,
  },
  {
    Header: () => (
      <HeaderCell header={PARCEL_NUMBER} classes={headerCellClasses} />
    ),
    Cell: ({ value, rowData }) => {
      const trackLink = (
        <a
          className={bodyCellClasses.link}
          target="_blank"
          rel="noopener noreferrer"
          href={`${UNIVERSE_TRACKING_LINK_BASE}${rowData.parcelCode}`}
        >
          {value}
        </a>
      );
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();
          onParcelNumberClick(value);
        },
        [value]
      );
      return (
        <BodyCell
          value={trackLink}
          classes={bodyCellClasses}
          onClick={onClick}
        />
      );
    },
    columnConfig: columnsConfigs[CA.PARCEL_NUMBER],
    accessor: CA.PARCEL_NUMBER,
  },
  {
    Header: () => <HeaderCell header={ADDRESS} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellCroppedText]}
      />
    ),
    columnConfig: columnsConfigs[CA.ADDRESS],
    accessor: CA.ADDRESS,
  },
  {
    Header: () => (
      <HeaderCell header={MANIFEST_LOCATION} classes={headerCellClasses} />
    ),
    Cell: ({ value }) => (
      <ManifestLocationItem
        value={value}
        classes={bodyCellClasses}
        maxCellValueWidth={4}
      />
    ),
    columnConfig: columnsConfigs[CA.MANIFEST_LOCATION],
    accessor: CA.MANIFEST_LOCATION,
  },
  {
    Header: () => <HeaderCell header={KING} classes={headerCellClasses} />,
    Cell: (row) => {
      const icon = <RadioIcon checked={row.value} />;
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();

          if (row.value) {
            return;
          }

          onUpdateKingParcel(row);
        },
        [row]
      );

      return (
        <BodyCell
          onClick={onClick}
          value={icon}
          classes={bodyCellClasses}
          disabled={isKingParcelColumnDisabled}
          data-testid={`${CA.KING}-icon-${row.value ? 'checked' : 'unchecked'}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.KING],
    accessor: CA.KING,
  },
  {
    Header: () => <HeaderCell header={CAD} classes={headerCellClasses} />,
    Cell: ({ value, rowData }) => {
      const icon = (
        <SpotIcon color={getCadScannedStatusColor(value, rowData)} />
      );
      return (
        <BodyCell
          value={icon}
          classes={bodyCellClasses}
          data-testid={`${CA.CAD_SCANNED}-icon-${getCadStatusTextColor(
            value,
            rowData
          )}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.CAD_SCANNED],
    accessor: CA.CAD_SCANNED,
  },
  {
    Header: () => <HeaderCell header={LOAD} classes={headerCellClasses} />,
    Cell: ({ value }) => {
      const icon = (
        <SpotIcon
          color={value ? palette.statuses.statusGreen : palette.dpdRed}
        />
      );
      return (
        <BodyCell
          value={icon}
          classes={bodyCellClasses}
          data-testid={`${CA.LOAD}-icon-${value ? 'green' : 'red'}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.LOAD],
    accessor: CA.LOAD,
  },
  {
    Header: () => <HeaderCell header={WINDOW} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.WINDOW],
    accessor: CA.WINDOW,
  },
  {
    Header: () => <HeaderCell header={TYPE} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellCroppedText]}
      />
    ),
    columnConfig: columnsConfigs[CA.TYPE],
    accessor: CA.TYPE,
  },
  {
    Header: () => <HeaderCell header={KG} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={gramsToKg(value.weight)}
        classes={bodyCellClasses}
        extraCellClasses={[
          value.actualWeight && bodyCellClasses.cellYellowText,
        ]}
      />
    ),
    columnConfig: columnsConfigs[CA.WEIGHT],
    accessor: CA.WEIGHT,
  },
  {
    Header: () => <HeaderCell header={CHUTE} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.CHUTE],
    accessor: CA.CHUTE,
  },
  {
    Header: () => <HeaderCell header={REQ} classes={headerCellClasses} />,
    Cell: ({ value, rowData }) => {
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();
          value?.length && onParcelRequestClick(rowData);
        },
        [rowData, value.length]
      );

      if (!value?.length && !rowData.routeOutstandingRequest?.length) {
        return null;
      }

      const request = value?.length ? head(value) : null;

      const bodyProps = {
        value: (() => {
          const requestTypeMap = {
            deferred: <Defer />,
            remove: <Remove />,
            add: <Add />,
          };

          if (request) {
            if (isDeferredRequest(request)) {
              return requestTypeMap.deferred;
            }
            if (isRemoveRequest(request)) {
              return requestTypeMap.remove;
            }
            if (!isRequestHasResponse(request)) {
              return requestTypeMap.add;
            }
          }
          if (rowData.routeOutstandingRequest?.length) {
            return (
              <WarningWithTooltip routes={rowData.routeOutstandingRequest} />
            );
          }
          return null;
        })(),
      };

      return (
        <BodyCell
          classes={bodyCellClasses}
          onClick={onClick}
          {...bodyProps}
          data-testid={
            request &&
            `${CA.REQUESTS}-icon-${getParcelRequestString(request)} || ${
              CA.REQUESTS
            }-icon-warning`
          }
        />
      );
    },
    columnConfig: columnsConfigs[CA.REQUESTS],
    accessor: CA.REQUESTS,
  },
];

export default parcelDetailsColumns;
